/**
 * GH #7583 — how much of the tenant's open work a client-built task list
 * (`allTasks`) holds, from `allTasksScope` (useAppState). Every surface that
 * counts `allTasks` reads this first, so a list whose workflow steps have not
 * loaded, failed to load, or hold only the viewer's own steps is never shown
 * as a complete total (TRYGGR_INTENT §5).
 *
 *   'complete' — every person's open steps, read whole
 *   'own'      — only the viewer's steps (not admin / manager, WF-B-003)
 *   'partial'  — the server reported steps it could not read, or truncated
 *   'loading'  — not read yet
 *   'failed'   — the read failed
 */
/**
 * The open-work list and its scope as one pair of props. A surface handed
 * `allTasks` without `allTasksScope` reads 'loading' forever, so the routed
 * pages' common props carry both through this helper, never one alone.
 */
export function openWorkProps({ allTasks, allTasksScope }) {
  return { allTasks, allTasksScope };
}

export function openWorkCompleteness(scope) {
  if (!scope || scope.workflowSteps === 'not-read') return 'loading';
  if (scope.workflowSteps === 'unavailable') return 'failed';
  if (scope.partial) return 'partial';
  return scope.workflowSteps === 'tenant' ? 'complete' : 'own';
}

/**
 * The `allTasksScope` shape from a `useOpenWorkflowSteps` view, for a surface
 * that reads the steps itself rather than through useAppState.
 */
export function openWorkScopeOf(view) {
  return {
    workflowSteps: view?.failed ? 'unavailable' : !view?.loaded ? 'not-read' : view.scope,
    partial: !!view?.partial,
  };
}

/**
 * The completeness of a per-viewer list read on its own (questions, AI risk
 * assessments) from its store status: 'ok' or 'none' (nothing to read) is the
 * whole list; 'loading' and 'failed' are not countable.
 */
export function readCompleteness(status) {
  if (status === 'failed') return 'failed';
  if (status === 'loading') return 'loading';
  return 'complete';
}

const LEAST_COUNTABLE_FIRST = ['failed', 'loading', 'partial', 'own', 'complete'];

/**
 * The completeness of one count taken over several lists (e.g. workflow steps
 * and questions on the calendar): the least countable of them, so one unread
 * list makes the whole count unknown.
 */
export function worstCompleteness(...all) {
  return LEAST_COUNTABLE_FIRST.find((c) => all.includes(c)) || 'complete';
}

/** True when a count of the list may be shown at all. */
export function isCountable(completeness) {
  return completeness === 'complete' || completeness === 'own' || completeness === 'partial';
}

/** A count of the list: "—" when unknown, "≥ n" when it is a lower bound. */
export function countOf(n, completeness) {
  if (!isCountable(completeness)) return '—';
  return completeness === 'complete' ? n : `≥ ${n}`;
}

const NOTICE = {
  loading: 'Workflow steps are still loading — these counts are not known yet.',
  failed: 'Workflow steps could not be read — these counts are unknown, not zero.',
  own: "Other people's workflow steps are not included — not the tenant's total.",
  partial: 'Some workflow steps could not be read — these counts are a lower bound.',
};

/** The one line every such surface shows when its counts are not the total. */
export function OpenWorkNotice({ completeness, what, style }) {
  if (completeness === 'complete') return null;
  // A list other than the workflow steps names itself in the same sentences.
  const text = what ? NOTICE[completeness].replace('Workflow steps', what) : NOTICE[completeness];
  return (
    <div
      role="status"
      data-testid="open-work-incomplete"
      data-completeness={completeness}
      style={{ fontSize: 11, color: 'var(--warn)', ...style }}
    >
      {text}
    </div>
  );
}
